/**
 * Thin async wrappers around existing stores/endpoints for the copilot
 * onboarding flow. No new business logic — each function adapts an existing
 * call. Functions marked "not yet wired" throw because there is no
 * client-side API to invoke MCP tools directly outside the chat/SSE
 * transport (see SseAgentTransport.js) — wire these once one exists.
 */
import { reaction } from "mobx";
import { makeApi, URLS } from "Utils/apiURL";
import deepResearchData from "Stores/deepResearchData";
import { settingsStoreData } from "Stores/settingsData";
import { userDetail } from "Stores/userDetail";
import { fetchConnectedEmails, fetchLinkedInAccounts } from "Utils/commonAPI";
import { dataSearchState, ADD_TO_LIST_UPGRADE_REQUIRED } from "Pages/Dashboard/dataSearchV2/state";
import customListState from "Pages/Dashboard/dataSearchV2/CustomList/state";
import { popupLoaderState } from "Pages/Dashboard/dataSearchV2/PopupLoader";
import { upgradePopupState } from "Components/Paywall/UpgradePopup";
import { mapIndustriesToListBuilder } from "Pages/Dashboard/DeepResearch/components/prospectFilterVocab";

// The list-builder's add/enrich paths pop the DataSearch page's own loader
// (popupLoaderState). In the copilot the onboarding shows its own loader, so
// that one just leaks in as a stray/empty popup — force it shut after our
// calls so it never appears over the chat.
const dismissDataSearchPopup = () => {
  try {
    popupLoaderState.setShowPopup(false);
  } catch (e) {
    /* best-effort */
  }
};

// Mirrors the default filter shape from dataSearchV2/state.js
// (createDefaultFilterComponentDataForImport, not exported there).
const createDefaultFilterComponentData = () => ({
  confidenceScore: [],
  excludeTeamOwns: true,
  industry: [],
  industryExclude: [],
  sector: [],
  employeeCount: [],
  revenue: [],
  department: [],
  funding: [],
  fundingTotalAmountStart: null,
  fundingTotalAmountEnd: null,
  fundingLastAmountStart: null,
  fundingLastAmountEnd: null,
  fundingDurationStart: null,
  fundingDurationEnd: null,
  employeesByDepartment: [],
  growthByDepartment: [],
  companyType: [],
  foundedYearStart: null,
  foundedYearEnd: null,
  technologies: [],
  level: [],
  title: [],
  titleExclude: [],
  skill: [],
  skillExclude: [],
  certification: [],
  certificationExclude: [],
  titleExactMatch: false,
  primaryActiveRoleOnly: true,
  lookalikeDomains: [],
  companyDomain: [],
  companyName: [],
  companyDomainList: [],
  companyNameList: [],
  companyLinkedIn: [],
  companyLinkedInList: [],
  companySearchType: "domain",
  companyKeywords: [],
  companyKeywordsExclude: [],
  contactKeywords: [],
  contactKeywordsExclude: [],
  profileBadge: [],
  country: [],
  state: [],
  city: [],
  companyCountry: [],
  companyState: [],
  companyCity: [],
  name: [],
  contactLinkedIn: [],
  contactSearchType: "name",
  excludeContactsMode: "list",
  excludeContactListId: null,
  excludeContactListName: "",
  excludeContactLinkedIn: []
});

// makeApi resolves (does not throw) on non-2xx, returning the axios error
// object, so success has to be asserted explicitly or a failed call reads as
// an empty-but-successful profile.
const isOk = res =>
  res && typeof res.status === "number" && res.status >= 200 && res.status < 300 && res.data;

export const enrichSeller = async website => {
  const res = await makeApi({
    url: URLS.deepResearchEnrichSeller,
    method: "POST",
    data: { website }
  });
  if (!isOk(res)) {
    const status = res?.status || res?.response?.status || "network";
    throw new Error(`enrichSeller failed (${status})`);
  }
  return res.data;
};

// Existing seller profiles, active one first. An account that already has
// profiles (an existing user, or a retry after a failed build) should be able
// to pick one instead of building from the website again.
export const listSellerProfiles = async () => {
  try {
    await deepResearchData.fetchSellerProfiles();
  } catch (e) {
    /* best-effort */
  }
  const activeId = deepResearchData.activeSellerProfileId;
  return (deepResearchData.sellerProfiles || [])
    .map(p => ({ ...p, id: p.id, name: p.name || p.company_name || "", active: p.id === activeId }))
    .filter(p => p.id)
    .sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0));
};

// Resolves free text like "use the salesgear" or "use my existing profile" to
// a profile: exact id, then a name mentioned in the text, then the active one
// when the text just says "existing"/"current"/"my profile".
export const findSellerProfile = async text => {
  const profiles = await listSellerProfiles();
  if (!profiles.length) return null;
  const t = String(text || "").toLowerCase();
  const byId = profiles.find(p => t.includes(String(p.id).toLowerCase()));
  if (byId) return byId;
  const byName = profiles.find(p => p.name && t.includes(p.name.toLowerCase()));
  if (byName) return byName;
  if (/\b(existing|current|active|my|default)\b/.test(t)) return profiles[0];
  return null;
};

export const saveSeller = async profile => {
  return deepResearchData.saveSellerProfile(profile);
};

export const mapIcpToFilters = icpFilters => {
  const { title, level, industry, employeeCount, country } = icpFilters || {};
  return {
    ...createDefaultFilterComponentData(),
    title: title || [],
    level: level || [],
    industry: industry || [],
    employeeCount: employeeCount || [],
    country: country || [],
    excludeTeamOwns: true,
    primaryActiveRoleOnly: true
  };
};

export const runSearch = async icpFilters => {
  // Industries are validated against the live List Builder vocabulary (the
  // same path Seller Intelligence's "Find prospects" uses); raw LinkedIn-style
  // names the search does not know are dropped rather than silently mismatched.
  const industry = await mapIndustriesToListBuilder((icpFilters || {}).industry);
  dataSearchState.setFilterComponentData(mapIcpToFilters({ ...icpFilters, industry }));
  // Mirror the countries as location chips. The list builder wipes
  // filterComponentData.country on load whenever selectedFilteredList.location
  // is empty, which silently broadened the "Refine in list builder" search
  // (2,811 matches became 46.6K). Industry likewise: the left panel's badge
  // counts selectedFilteredList.industry. Mirror the MAPPED industries, not the
  // raw ones, or the chips disagree with the filter actually applied.
  const chips = arr => (arr || []).map(v => ({ id: String(v).toLowerCase(), name: v, checked: true }));
  dataSearchState.setSelectedFilteredList({
    ...(dataSearchState.selectedFilteredList || {}),
    location: chips(icpFilters && icpFilters.country),
    industry: chips(industry)
  });
  await dataSearchState.applyFiltersAndSearch();
  const { totalContacts = 0, contacts = [] } = dataSearchState.contactTableData || {};
  return { total: totalContacts, rows: contacts };
};

// Maps a discover contact row to the standard prospect columns the copilot
// table renders, keeping the identity fields the add-to-list path needs.
const mapProspect = c => ({
  id: c.id || c.aiArkId || undefined,
  firstName: c.firstName || "",
  lastName: c.lastName || "",
  linkedIn: c.linkedIn || c.linkedin || c.link || "",
  domain: c.companyDomain || c.primaryDomain || c.companyWebsite || "",
  companyLinkedIn: c.companyLinkedIn || undefined,
  city: c.city || "",
  state: c.state || "",
  country: c.country || "",
  name: [c.firstName, c.lastName].filter(Boolean).join(" ") || c.name || "",
  title: c.title || "",
  company: c.companyName || c.company || "",
  industry: c.companyIndustry || c.industry || "",
  location: [c.city, c.state, c.country].filter(Boolean).join(", ")
});

// Serves the prospect TABLE_REF shown in Step 4, paging the live discover
// search so the table shows EVERY matching record in view mode, exactly like
// the discover prospect list. Viewing pages reveals nothing, so no contact
// credits are spent (it counts an lb-search per page, same as the discover
// page's own paging). Returns the full match total so paging works end to end.
// The rows the scripted search already loaded, trimmed to what an agent needs
// to act on them. Without these it re-ran the search from scratch (and once
// came back with 0 rows where the scripted search had found 1,110). linkedIn is
// included because addRowsToList treats it as the identity key: a row without
// it cannot be added.
export const PROSPECT_BRIEF_CAP = 25;

export const loadedProspects = (cap = PROSPECT_BRIEF_CAP) => {
  const { contacts = [] } = dataSearchState.contactTableData || {};
  return contacts.slice(0, cap).map(c => {
    const m = mapProspect(c);
    return {
      name: m.name,
      title: m.title,
      company: m.company,
      linkedIn: m.linkedIn,
      location: m.location
    };
  });
};

export const fetchProspectPage = async (tableId, page = 1, pageSize) => {
  const sizeChanged = pageSize && dataSearchState.pageLimit !== pageSize;
  const loaded = dataSearchState.contactTableData?.contacts?.length || 0;
  if (sizeChanged) dataSearchState.setPageLimit(pageSize);
  if (sizeChanged || page !== dataSearchState.currentPage || !loaded) {
    dataSearchState.setCurrentPage(page);
    await dataSearchState.searchPageData();
  }
  const { contacts = [], totalContacts = 0 } = dataSearchState.contactTableData || {};
  return {
    rows: contacts.map(mapProspect),
    totalCount: totalContacts,
    page: dataSearchState.currentPage,
    pageSize: dataSearchState.pageLimit
  };
};

// Adds specific selected prospect rows (from the popup table) to a list, so a
// user who hand-picks contacts and clicks "Add to list" gets exactly those.
// Mirrors dataSearchV2's buildAddToDirectoryPayload shape. Returns {added, skipped}.
// The popup add-to-list path only carries the list id, so the handoff had no
// name to hand the sequence builder. The picker has already loaded the lists.
// Stored artifact ids are HINTS, not truth. A list id can point at a list the
// user deleted, or at one the bulk save left empty (observed: the flow reported
// "359 contacts saved" against a list that actually had 0). Resume must verify
// before it skips a step, or it strands the user past work that never happened.
export const verifyArtifacts = async (artifacts = {}) => {
  const out = { ...artifacts };
  const drop = [];

  if (out.savedProfileId) {
    try {
      const profiles = await deepResearchData.fetchSellerProfiles({ force: true });
      const hit = (profiles || []).find(p => String(p.id) === String(out.savedProfileId));
      if (!hit) { drop.push("sellerProfile"); out.savedProfileId = ""; }
    } catch (e) { /* unreachable backend: keep the hint rather than guess */ }
  }

  if (out.listId) {
    try {
      // Refresh first. On a reload the store is empty until something fetches
      // it, so this read used to see zero lists, drop nothing, and resume
      // straight onto a list that no longer had any contacts in it.
      await customListState.getContactList();
      const all = customListState.totalContactList || [];
      const hit = all.find(l => String(l.id || l._id) === String(out.listId));
      // `count` is the field the list store actually carries (getListFillStatus
      // and snapshotLists both read it); `contacts`/`contactCount` are not set,
      // so every list read as empty and was dropped.
      const count = hit ? Number(hit.count ?? 0) : null;
      if (hit && Number.isFinite(count)) out.listSize = count;
      // Present but empty is the same as not done: the save did not land.
      if (hit && count === 0) { drop.push("emptyList"); out.listId = ""; out.listSize = 0; }
      if (!hit && all.length) { drop.push("missingList"); out.listId = ""; out.listSize = 0; }
    } catch (e) { /* keep the hint */ }
  }

  return { artifacts: out, dropped: drop };
};

export const getListName = listId => {
  if (!listId) return "";
  const all = customListState.totalContactList || [];
  const hit = all.find(l => String(l.id || l._id) === String(listId));
  return (hit && (hit.name || hit.listName)) || "";
};

export const addRowsToList = async (listId, rows) => {
  const stripHttp = u => String(u || "").replace(/^https?:\/\//i, "").trim();
  const mapped = (rows || [])
    .map(r => {
      const li = stripHttp(r.linkedIn || r.linkedin || r.link);
      if (!li) return null;
      return {
        firstName: r.firstName || null,
        lastName: r.lastName || null,
        company: r.company || null,
        domain: r.domain ? String(r.domain).trim() : null,
        jobTitle: r.title || null,
        industry: r.industry || null,
        linkedinId: li,
        email: null,
        city: r.city || null,
        state: r.state || null,
        country: r.country || null,
        liCompanyId: r.companyLinkedIn || null,
        aiArkId: r.id || null
      };
    })
    .filter(Boolean);
  const skipped = (rows || []).length - mapped.length;
  if (!mapped.length) return { added: 0, skipped };
  const res = await makeApi({
    url: URLS.addContactsToDirectory,
    method: "POST",
    data: { contacts: mapped, contactTags: [], listId: listId ? String(listId) : null, useSnCredits: true }
  });
  if (!isOk(res)) return { added: 0, skipped: (rows || []).length, error: true };
  const data = res.data || {};
  const added = (data.validContacts != null ? data.validContacts : mapped.length);
  return { added, skipped };
};

export const loosenSearch = async icpFilters => {
  const loosened = { ...icpFilters };
  let dropped = null;
  if (loosened.industry?.length) {
    loosened.industry = [];
    dropped = "industry";
  } else if (loosened.level?.length) {
    loosened.level = [];
    dropped = "level";
  }
  const { total, rows } = await runSearch(loosened);
  return { total, rows, dropped };
};

// Save the matched prospects to a real list. addContactToList adds either the
// current selection or, for a bulk/range selection, up to `limit` from the
// search. Onboarding never touches the checkboxes, so drive the bulk path with
// an explicit cap. Aligned to the copilot's save contract: 100 per save.
export const LIST_SAVE_CAP = 100;

export const saveList = async (name, filterComponentData, total) => {
  const cap = Math.min(total || LIST_SAVE_CAP, LIST_SAVE_CAP) || LIST_SAVE_CAP;
  // createContactList returns undefined on a name collision (409) or other
  // failure. Don't report "saved" on that: retry once with a unique name so a
  // repeat run doesn't dead-end on "list name already exists".
  let list = await customListState.createContactList(name);
  let finalName = name;
  if (!list) {
    finalName = `${name} ${Date.now().toString().slice(-4)}`;
    list = await customListState.createContactList(finalName);
  }
  if (!list) return { ok: false, listId: "", listName: name, listSize: 0 };
  let noCredits = false;
  try {
    dataSearchState.rangeSelected = { type: "bulk", limit: cap };
    await dataSearchState.addContactToList(list);
  } catch (e) {
    // Out of contact credits: addContactToList opens the list builder's
    // upgrade modal and throws this code. Swallowing it as "best-effort" and
    // reporting "saved, adding in the background" left the status at 0 of 100
    // forever. Everything else really is best-effort (the list exists).
    noCredits = e && e.code === ADD_TO_LIST_UPGRADE_REQUIRED;
  } finally {
    dismissDataSearchPopup();
    // The bulk range was only for this save. Left set, the discover page's
    // purchase path later enriched the whole search instead of the list.
    try {
      dataSearchState.setRangeSelected("");
    } catch (e) {
      /* best-effort */
    }
  }
  if (noCredits || upgradePopupState.showPopup) {
    // The copilot owns this message; the modal would just sit over the chat.
    upgradePopupState.setPopup(false);
    return {
      ok: false,
      reason: "insufficient_credits",
      listId: list.id || list._id || list.listId || "",
      listName: list.listName || finalName,
      listSize: 0
    };
  }
  return {
    ok: true,
    listId: list.id || list._id || list.listId || "",
    listName: list.listName || finalName,
    listSize: cap
  };
};

// ── Background job tracking ────────────────────────────────────────────────
// Add-to-list and enrich both run as BulkOperationJobs on the server. The
// perform calls do not return the job id, so a job is found as the newest one
// of its type created after we fired it. POST /cm/bulk/logs pages the team's
// jobs newest-first: { jobs: [{ id, type, status, totalContacts,
// processedContacts, createTs }] }, status TO_BE_PROCESSED | IN_PROGRESS |
// PROCESSED | FAILED.
export const fetchBulkJobs = async (type, limit = 20) => {
  const res = await makeApi({
    url: URLS.tasksOperationLogs,
    method: "POST",
    data: { pageNo: 1, limit, ...(type ? { type } : {}) }
  });
  return isOk(res) && Array.isArray(res.data.jobs) ? res.data.jobs : [];
};

const jobTs = j => new Date(j.createTs || j.createdAt || j.createdTs || 0).getTime();

export const findJobSince = async (type, since) => {
  const jobs = await fetchBulkJobs(type).catch(() => []);
  // 5s slack: the job's server timestamp can precede our client clock.
  const hit = jobs
    .filter(j => jobTs(j) >= (since || 0) - 5000)
    .sort((a, b) => jobTs(b) - jobTs(a))[0];
  if (!hit) return null;
  const status = String(hit.status || "").toUpperCase();
  const total = Number(hit.totalContacts) || 0;
  const processed = Number(hit.processedContacts) || 0;
  return {
    id: String(hit.id || hit._id || ""),
    status,
    total,
    processed,
    done: status === "PROCESSED",
    failed: status === "FAILED",
    percent: total ? Math.min(100, Math.round((processed / total) * 100)) : status === "PROCESSED" ? 100 : 0
  };
};

// Reads how far the background bulk job has filled the saved list, by
// refreshing the contact lists and reading the target list's contact count.
// Some matches are skipped (no LinkedIn etc.), so the job can settle below the
// requested cap; the caller treats a plateau as "done" as well as reaching it.
export const getListFillStatus = async (listId, expected, since) => {
  if (!listId) return { added: 0, expected: expected || 0, percent: 0, done: true };
  try {
    await customListState.getContactList();
  } catch (e) {
    /* best-effort refresh */
  }
  const all = customListState.totalContactList || [];
  const hit = all.find(l => String(l.id || l._id) === String(listId));
  const added = (hit && Number(hit.count)) || 0;
  const target = expected || added;
  // Prefer the job itself when we know when it started: it knows "failed" and
  // "finished below the cap" (skips), which a count alone cannot tell apart
  // from "still running".
  const job = since ? await findJobSince("ADD_TO_LIST", since) : null;
  if (job) {
    const percent = job.done ? 100 : job.percent || (target ? Math.min(100, Math.round((added / target) * 100)) : 0);
    return { added, expected: target, percent, done: job.done, failed: job.failed, job };
  }
  const percent = target ? Math.min(100, Math.round((added / target) * 100)) : added > 0 ? 100 : 0;
  return { added, expected: target, percent, done: target > 0 && added >= target };
};

// Refine hands the user to the list builder, where the copilot cannot see what
// they do. Snapshot the lists when they leave; on return, the list they added
// to is the one created since, or whose count grew.
export const snapshotLists = async () => {
  try {
    await customListState.getContactList();
  } catch (e) {
    /* best-effort */
  }
  return (customListState.totalContactList || []).map(l => ({
    id: String(l.id || l._id),
    count: Number(l.count) || 0
  }));
};

export const findListChangedSince = async (snapshot, since) => {
  try {
    await customListState.getContactList();
  } catch (e) {
    /* best-effort */
  }
  const before = new Map((snapshot || []).map(l => [l.id, l.count]));
  const changed = (customListState.totalContactList || []).filter(l => {
    const id = String(l.id || l._id);
    const count = Number(l.count) || 0;
    const created = new Date(l.createTs || 0).getTime();
    return (!before.has(id) && created >= (since || 0)) || count > (before.get(id) || 0);
  });
  if (!changed.length) return null;
  changed.sort((a, b) => new Date(b.createTs || 0) - new Date(a.createTs || 0));
  const hit = changed[0];
  return { id: String(hit.id || hit._id), name: hit.name || hit.listName || "", count: Number(hit.count) || 0 };
};

// Fires cb(total) each time the list builder runs a new search (the result
// total changes), so the docked copilot can react to a refinement without the
// user having to tell it. Returns the disposer.
export const watchSearchTotal = cb =>
  reaction(
    () => dataSearchState.contactTableData?.totalContacts || 0,
    total => cb(total)
  );

// The list builder's live result count, for saving a refined search.
export const currentSearchTotal = () => dataSearchState.contactTableData?.totalContacts || 0;

export const getCredits = async () => {
  // Credits are a single general pool spent for every purpose (enrich included).
  return {
    remaining: settingsStoreData.totalRemainingCredits || 0,
    totalRemaining: settingsStoreData.totalRemainingCredits || 0,
    email: 1,
    phone: 10
  };
};

// The saved list's contact ids (onboarding lists are capped at 100). Same
// session endpoint the Contacts page lists with.
export const fetchListContactIds = async (listId, limit = LIST_SAVE_CAP) => {
  const res = await makeApi({
    url: URLS.getAllContacts,
    method: "POST",
    data: { filter: { stage: null }, searchTerm: "", listId: String(listId), pageNo: 1, limit }
  });
  const contacts = (isOk(res) && Array.isArray(res.data.contacts) && res.data.contacts) || [];
  return contacts.map(c => c && (c.id || c._id)).filter(Boolean);
};

// Enrich exactly the contacts in the saved list. This used to call the
// discover page's purchase (purchaseBulkContact), which enriches the page's
// current selection/bulk range -- i.e. the whole search, not the list: one
// run purchased 1,357 contacts for a 2-contact list. Now it runs the same
// ENRICH_CONTACT bulk job the Contacts page uses, scoped by contact ids, so
// it is a background BulkOperationJob the enrich-status step can track.
export const enrichContacts = async (type, listId) => {
  try {
    const ids = listId ? await fetchListContactIds(listId) : [];
    if (!ids.length) return { ok: false, reason: "empty_list", message: "No contacts in the list yet" };
    const res = await makeApi({
      url: URLS.performContactsBulkOperation,
      method: "POST",
      data: {
        type: "ENRICH_CONTACT",
        all: false,
        totalCount: ids.length,
        filter: { contactIds: ids },
        enrichContactPayload: { enrichType: type === "EMAIL_PHONE" ? "EMAIL_PHONE" : "EMAIL" }
      }
    });
    const data = (res && res.data) || {};
    const code = Number(data.responseCode || data.statusCode || (res && res.status));
    // 204 daily email limit, 205 no phone credits, 402/409 out of credits.
    if ([204, 205, 402, 409].includes(code)) {
      return { ok: false, reason: "insufficient_credits", remaining: data.monthlyEmailCreditsRemaining || 0, code };
    }
    if (code === 207) return { ok: false, message: `Too many contacts to enrich at once (limit ${data.limit || ""})` };
    if (!isOk(res) || data.success === false) return { ok: false, message: data.message || "Enrichment could not start" };
    return { ok: true, message: "Enrichment started", count: ids.length, background: !!data.background };
  } catch (err) {
    return { ok: false, message: err?.message || "Enrichment failed" };
  } finally {
    dismissDataSearchPopup();
  }
};

// Add the saved list's contacts to the created sequence as an ADD_TO_SEQUENCE
// bulk job (same call the Contacts page makes). Deterministic and card-free:
// the onboarding already asked everything it needs to.
export const enrolListInSequence = async (listId, sequenceId) => {
  try {
    const ids = listId ? await fetchListContactIds(listId) : [];
    if (!ids.length) return { ok: true, count: 0 };
    const res = await makeApi({
      url: URLS.performContactsBulkOperation,
      method: "POST",
      data: {
        type: "ADD_TO_SEQUENCE",
        all: false,
        totalCount: ids.length,
        filter: { contactIds: ids },
        data: { sequenceId: String(sequenceId) }
      }
    });
    const data = (res && res.data) || {};
    const code = Number(data.responseCode || data.statusCode || (res && res.status));
    if (!isOk(res) || data.success === false || (code && code >= 400)) {
      return { ok: false, count: 0, message: data.message || "Couldn't add the contacts to the sequence" };
    }
    return { ok: true, count: ids.length, background: !!data.background };
  } catch (err) {
    return { ok: false, count: 0, message: err?.message || "Couldn't add the contacts to the sequence" };
  }
};

// An inbox counts only when it is ACTIVE: revoked or disconnected inboxes stay
// in the list, and counting them told the user "your inbox is connected"
// right before the campaign step refused for the same reason.
export const connectStatus = () => {
  const inboxes = userDetail.connectedInboxes || [];
  return {
    email: inboxes.some(i => String((i && i.status) || "").toUpperCase() === "ACTIVE"),
    linkedIn: (userDetail.activeLinkedInAccounts?.length || 0) > 0
  };
};

// Every gate reads a fresh list, so two steps seconds apart cannot disagree.
export const refreshConnectStatus = async () => {
  try {
    await Promise.all([fetchConnectedEmails(), fetchLinkedInAccounts()]);
  } catch (e) {
    /* fall back to whatever the store holds */
  }
  return connectStatus();
};

// Creates a real DRAFT sequence via the native web endpoint (same POST /cm/seq
// the sequence page uses). The draft starts with no steps: onboarding never
// sends, and step copy + contact enrolment are done on the sequence detail
// page, where the product's own AI writer and enrolment live. We return the
// real step count from the response (0 for a fresh draft) rather than a
// hardcoded promise, so the handoff cannot claim steps that do not exist.
const createSequenceDraft = async name => {
  const payload = {
    accessType: "READ_WRITE",
    bulk: false,
    connectedEmail: null,
    currentStage: null,
    folderId: null,
    folderName: "My Sequences",
    shareType: "PRIVATE",
    stages: {},
    type: "New Sequence",
    name
  };
  const res = await makeApi({ url: URLS.createSequence, method: "POST", data: payload });
  if (!isOk(res) || !res.data?.id) {
    const status = res?.status || res?.response?.status || "network";
    throw new Error(`createSequence failed (${status})`);
  }
  const seq = res.data;
  const stepCount = Array.isArray(seq.stages)
    ? seq.stages.length
    : seq.stages && typeof seq.stages === "object"
      ? Object.keys(seq.stages).length
      : 0;
  return { sequenceId: seq.id, steps: stepCount };
};

export const createEmailSequence = async name => {
  const { sequenceId, steps } = await createSequenceDraft(name);
  return { sequenceId, steps };
};

export const createDeepResearchSequence = async name => {
  const { sequenceId, steps } = await createSequenceDraft(name);
  return { sequenceId, steps, deepResearch: true };
};

// Seeds the editable ICP card from the enriched seller profile. There is no
// structured ICP on the profile (titles/industries/size/geo live only as
// list-builder filters), so this is a light client heuristic; the user edits
// the card before the Step 4 search runs, per the spec.
export const deriveIcp = async profile => {
  const p = profile || {};
  const industry = []
    .concat(p.segments || [], p.industries || [])
    .filter(Boolean)
    .slice(0, 5);
  const country = countryFromLocation(p.location);
  return { title: [], level: [], department: [], industry, employeeCount: [], country };
};

// Generates the seller intelligence (buyer personas, ICP, competitor map)
// for a just-saved profile. Same endpoint the Seller Profile page calls after
// save; ~7s. Null on failure so the caller can fall back to deriveIcp.
export const deriveSellerIntelligence = async sellerProfileId => {
  if (!sellerProfileId) return null;
  const res = await makeApi({
    url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(sellerProfileId)}/derive`,
    method: "POST",
    data: {}
  });
  const d = res?.data;
  return d && d.ok && d.intelligence ? d.intelligence : null;
};

const LEVEL_FROM_SENIORITY = [
  // Ids are the List Builder's level ids ("c_suite", not "cxo": an unknown id
  // drops the whole level filter and titles alone match anyone).
  [/founder/i, "founder"],
  [/owner/i, "owner"],
  [/c[- ]?suite|cxo|c-level|chief|president/i, "c_suite"],
  [/\bs?vp\b|vice president/i, "vp"],
  [/director/i, "director"],
  [/head|manager|lead/i, "manager"]
];

// Intelligence stores employee ranges as "51 - 100"; the search filter uses
// four coarse buckets. Map by the range's lower bound.
const sizeBucket = range => {
  const n = parseInt((String(range || "").match(/\d+/) || [])[0], 10);
  if (!n) return null;
  if (n <= 50) return "1-50";
  if (n <= 200) return "51-200";
  if (n <= 1000) return "201-1000";
  return "1001-10000";
};

// The real ICP: read off the seller intelligence (canonical buyer personas +
// its ICP block) rather than asked as a questionnaire. Shape matches
// mapIcpToFilters so it feeds the list-builder search directly.
// "Chief Revenue Officer (CRO)" -> "chief revenue officer"; "Founder/CEO" ->
// "founder", "ceo". Persona titles are the search's title phrases; the
// persona title_keywords ("sales", "growth", "head") are too broad to filter on.
const cleanTitles = raw =>
  String(raw || "")
    .replace(/\([^)]*\)/g, "")
    .split("/")
    .map(t => t.trim().toLowerCase())
    .filter(t => t.length > 1);

// Location strings arrive like "Singapore (Corporate Office), United States
// (Contact Info)"; keep the last plain place name.
const countryFromLocation = location =>
  String(location || "")
    .replace(/\([^)]*\)/g, "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean)
    .slice(-1);

// "Global", "Worldwide", "Remote" and friends are how intelligence expresses
// "no particular market". They are not countries, so passing them through as a
// country filter matches nothing and the search returns zero. The questionnaire
// path already maps Global to an empty country list; do the same here.
const NON_COUNTRY = /^(global|worldwide|world ?wide|international|remote|anywhere|n\/?a|various|multiple)$/i;
const realCountries = list => (list || []).filter(c => c && !NON_COUNTRY.test(String(c).trim()));

export const icpFromIntelligence = (intel, profile) => {
  const personas = (intel && intel.canonical_buyer_personas) || [];
  const icp = (intel && (intel.ideal_customer_profile || intel.icp)) || {};
  const uniq = arr => Array.from(new Set(arr.filter(Boolean)));
  const title = uniq(personas.flatMap(p => cleanTitles(p.title))).slice(0, 8);
  const level = uniq(
    personas.map(p => {
      const s = `${p.seniority || ""} ${p.title || ""}`;
      const hit = LEVEL_FROM_SENIORITY.find(([re]) => re.test(s));
      return hit ? hit[1] : null;
    })
  );
  const industry = uniq(icp.industries || []).slice(0, 5);
  const employeeCount = uniq((icp.employee_ranges || []).map(sizeBucket));
  // Intelligence now carries the seller's markets; the profile's free-text
  // location is only the fallback.
  const hqCountries = realCountries(icp.hq_locations);
  const country = hqCountries.length
    ? uniq(hqCountries).slice(0, 3)
    : realCountries(countryFromLocation((profile || {}).location));
  // Department is kept for the summary only; it is not a reliable search key
  // ("Executive" is a persona grouping, not a list-builder department).
  const department = uniq(personas.map(p => p.department));
  return { title, level, department, industry, employeeCount, country };
};

export const hasIcp = icp =>
  !!icp && ["level", "department", "title", "industry"].some(k => (icp[k] || []).length > 0);
